import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, TextField, MenuItem } from "@mui/material";
import { useField, useFormikContext } from "formik";
import { convertToInteger } from "../../utils/string";

export const timeRangeOptions = [
  {
    value: "days",
    label: "days"
  },
  {
    value: "weeks",
    label: "weeks"
  },
  {
    value: "months",
    label: "months"
  }
];

const days = "days";
const months = "months";
const weeks = "weeks";

const isInMonths = value => value >= 30 && value % 30 === 0;
const isInWeeks = value => value >= 7 && value % 7 === 0;

const getDefaultValue = defaultValue => {
  if (isInMonths(defaultValue)) {
    return defaultValue / 30;
  } else if (isInWeeks(defaultValue)) {
    return defaultValue / 7;
  } else {
    return defaultValue;
  }
};

const TimeInterval = ({ name, label, defaultValue }) => {
  const { t } = useTranslation();
  const [intervalUnit, setIntervalUnit] = useState("");
  const [intervalValue, setIntervalValue] = useState(0);
  const [, { error }, { setValue }] = useField({ name });

  const { isSubmitting } = useFormikContext();

  const handleChange = (newValue, unit) => {
    const newTimeValue = convertToInteger(newValue);
    setIntervalValue(newTimeValue);
    if (unit === weeks) {
      setValue(newTimeValue * 7);
    } else if (unit === months) {
      setValue(newTimeValue * 30);
    } else {
      setValue(newTimeValue);
    }
  };

  useEffect(() => {
    if (isInMonths(defaultValue)) {
      setIntervalUnit(months);
    } else if (isInWeeks(defaultValue)) {
      setIntervalUnit(weeks);
    } else {
      setIntervalUnit(days);
    }
    setIntervalValue(getDefaultValue(defaultValue));
  }, [defaultValue]);

  const handleIntervalUnitChange = newIntervalUnit => {
    setIntervalUnit(newIntervalUnit);
    handleChange(intervalValue, newIntervalUnit);
  };

  return !!intervalUnit ? (
    <Box display="flex">
      <Box flex={1}>
        <TextField
          value={intervalValue}
          onChange={ev => handleChange(ev.target.value, intervalUnit)}
          variant="filled"
          InputProps={{
            disableUnderline: true,
            inputProps: {
              "data-qa": "time-interval-value"
            }
          }}
          fullWidth
          label={label}
          disabled={isSubmitting}
          helperText={!!error ? error : ""}
          error={!!error}
          hiddenLabel={!label}
        />
      </Box>
      <Box minWidth={190} ml={2}>
        <TextField
          select
          value={intervalUnit}
          onChange={ev => handleIntervalUnitChange(ev.target.value)}
          variant="filled"
          InputProps={{
            disableUnderline: true,
            inputProps: {
              "data-qa": "time-interval-unit"
            }
          }}
          fullWidth
          disabled={isSubmitting}
          hiddenLabel
        >
          {timeRangeOptions.map(({ value, label }) => (
            <MenuItem value={value} key={value}>
              {t(`timeRangeLabel.${label}`)}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  ) : null;
};

export default TimeInterval;
